import {FaCheck, FaPlus, FaSave, FaTimes, FaTrash} from 'react-icons/fa';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import TemplateDefault from "../../components/template-default";
import SelectComponent from "../../components/select-component";
import {useQueries} from "@tanstack/react-query";
import projectService from "../../services/project-service";
import Card from "../../components/card";
import Button from "../../components/button";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css"
import productService from "../../services/product-service";
import general from "../../utils/general";
import {modalities} from "./resources/modalities";
import {toast} from "react-toastify";
import {AlertMessage} from "../../utils/alert-message";
import {productInitialData} from "./resources/initial-data";
import MySwal from "sweetalert2";
import InputForm from "../../components/input-form";
import {useSearchParams} from "react-router-dom";
import {buildAccentInsensitiveColDef, setAccentInsensitiveQuickFilter} from "../../utils/ag-grid-quick-filter";

const Products = () => {
    const productRef = useRef();
    const [searchParams] = useSearchParams();

    const [project, setProject] = useState();
    const [products, setProducts] = useState([]);
    const [editedRows, setEditedRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const queries = useQueries({
        queries: [
            {
                queryKey: ['projects'],
                queryFn: () => {
                    return projectService.options().then(({data}) => data)
                },
            },
        ],

    });

    useEffect(() => {
        if (project?.value) {
            productService.getByProject(project.value)
                .then(data => {
                    setProducts(data);
                });

            productService.getDamages(project.value).then(({data}) => {
                const damagesType = data.map(t => t.name);
                damagesType.unshift(null);
                damagesType.push('CLIENTE');
                addParamsToColumns("damaged_equipment", damagesType);
            });
        }
    }, [project]);

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
        productService.getTypes().then(({data}) => {
            const typesMap = data.map(t => t.name);
            typesMap.unshift(null);
            addParamsToColumns("type", typesMap);
        });
    }, []);

    const addParamsToColumns = (field, values) => {
        setColumnDefs((prevColumnDefs) =>
            prevColumnDefs.map((colDef) => {
                if (colDef.field === field) {
                    return {
                        ...colDef,
                        cellEditorParams: {values}
                    };
                }
                return colDef;
            })
        );
    }

    const projects = queries[0].data;

    useEffect(() => {
        if (!projects || !searchParams.get("ibge")) return;

        const findProject = projects.find(p => p.code === searchParams.get("ibge"))
        setProject(findProject)
    }, [projects, searchParams]);

    const [columnDefs, setColumnDefs] = useState([
        {
            field: "answer", headerName: "Podemos atender?", width: 150, cellEditor: 'agCheckboxCellEditor',
            cellRenderer: params => {
                return (
                    <span className={"m-auto"}>
                        {
                            params.value ? <FaCheck className={"text-green-600"}/> :
                                <FaTimes className={"text-red-600"}/>
                        }
                    </span>
                );
            }
        },
        {
            field: "type",
            headerName: "Tipo",
            width: 100,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {values: []}
        },
        {
            field: "pcl_id",
            headerName: "ID PCL",
            width: 100,
            cellEditor: 'agNumberCellEditor',
            cellEditorParams: {min: 0}
        },
        {field: "place", headerName: "Localização", cellEditor: 'agTextCellEditor', cellEditorParams: {min: 0}},
        {
            field: "damaged_equipment",
            headerName: "Devolução",
            width: 150,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {values: []}
        },
        {
            field: "supply_equipment", headerName: "Reposição", width: 100, cellEditor: 'agCheckboxCellEditor',
            cellRenderer: params => {
                return (
                    <span className={"m-auto"}>
                        {
                            params.value ? <FaCheck className={"text-green-600"}/> :
                                <FaTimes className={"text-red-600"}/>
                        }
                    </span>
                );
            }
        },
        {
            field: "date_install", headerName: "Instalação", cellEditor: "agDateCellEditor", width: 120,
            cellRenderer: (params) => {
                return general.formatDateFromAgGrid(params.value)
            }
        },
        {
            field: "date_warranty", headerName: "Venc. garantia", width: 120, cellEditor: 'agDateCellEditor',
            cellRenderer: params => {
                return general.formatDateFromAgGrid(params.value)
            }
        },
        {
            field: "modality", headerName: "Modalidade", width: 150, cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
                values: modalities.map(m => m.value),
            },
            cellRenderer: (params) => {
                const option = modalities.find(m => m.value === params.value);
                return option ? option.label : params.value;
            }
        },
        {
            field: "date_until", headerName: "Até quando", width: 120, cellEditor: 'agDateCellEditor',
            cellRenderer: params => {
                return general.formatDate(params.value, "DD/MM/YYYY")
            }
        },
        {
            field: "", sortable: false, width: 60, editable: false,
            cellRenderer: (params) => {
                return (<span onClick={() => removeProduct(params)}
                              className="w-[60px] h-full flex justify-center items-center text-white bg-red-600 cursor-pointer rounded-md me-[6px]">
                            <FaTrash/>
                        </span>)
            }
        }
    ]);

    const removeProduct = (params) => {
        const {id, place} = params.data;

        if (id) {
            MySwal.fire({
                icon: "warning",
                title: "Excluir!",
                showCancelButton: true,
                text: `Tem certeza que deseja excluir o produto ${place ?? ""}?`,
                confirmButtonText: "Sim",
                cancelButtonText: "Não",
                confirmButtonColor: '#e64942',
                reverseButtons: true
            }).then(({isConfirmed}) => {
                if (isConfirmed) {
                    setIsLoading(true);

                    productService.delete(id)
                        .then(() => {
                            productRef.current?.api.applyTransaction({remove: [params.data]});
                            toast.success("Produto removido com sucesso.")
                        })
                        .catch((err) => {
                            const {data: {message}} = err.response;
                            toast.error(AlertMessage[message] ?? "Não foi possível excluir");
                        })
                        .finally(() => setIsLoading(false));
                }
            });
        } else {
            productRef.current?.api.applyTransaction({remove: [params.data]});
        }
    }

    const defaultColDef = useMemo(() => buildAccentInsensitiveColDef({
        resizable: true,
        editable: true,
        cellDataType: false,
    }), []);

    const onCellValueChanged = async (event) => {
        let {column, data} = event;
        const {colId} = column;
        const placeOld = data.place;

        switch (colId) {
            case "type":
                if (!checkType(data.type)) {
                    data.pcl_id = null;
                } else if (data.pcl_id) {
                    await updateData(data);
                }
                break;

            case "pcl_id":
                if (checkType(data.type)) {
                    await updateData(data);

                    if (data.place == null) {
                        data.place = placeOld;
                    }
                } else {
                    toast.error('Somente para tipo "PCL".');
                    data.pcl_id = null;
                }
                break;
            default:
                break;
        }

        productRef.current?.api.redrawRows();

        const updatedData = event.data;
        setEditedRows((prevEditedRows) => {
            const existingRow = prevEditedRows.find(row => row.id === updatedData.id);
            if (existingRow) {
                return prevEditedRows.map(row => row.id === updatedData.id ? updatedData : row);
            } else {
                return [...prevEditedRows, updatedData];
            }
        });
    }

    const checkType = (type) => {
        if (type) {
            if (type.toLowerCase().includes('pcl')) {
                return true;
            }
        }

        return false;
    }

    const updateData = async (data) => {
        try {
            const place = await loadPlace(data.pcl_id);
            const {capturePlace} = place;
            data.place = capturePlace.name || capturePlace.place;
            data.lat = capturePlace.lat;
            data.lng = capturePlace.lng;
        } catch (err) {
            data.place = null;
            data.lat = null;
            data.lng = null;
        }

        return data;
    }

    const loadPlace = async (pclId) => {
        return productService.getPlace({pclId, projectId: project.value})
            .then(({data}) => data)
            .catch(err => {
                console.error('Place not found');
            })
    }

    const save = () => {
        setIsLoading(true);

        editedRows.forEach(p => {
            p.date_install = general.formatDateToSave(p.date_install);
            p.date_warranty = general.formatDateToSave(p.date_warranty);
            p.date_until = general.formatDateToSave(p.date_until);
        });

        productService.saveBatch({data: editedRows, project_id: project?.value})
            .then(({data}) => {
                setProducts(data);
                setEditedRows([]);
                toast.success("Salvo com sucesso.")
            })
            .catch(error => {
                const {data: {message}} = error.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
            .finally(() => setIsLoading(false))
    }

    const addProduct = useCallback(() => {
        productRef.current?.api.applyTransaction({add: [{...productInitialData}], addIndex: 0});

        productRef.current.value = null;
    }, [productRef]);

    const onFilterTextBoxChanged = useCallback(({target}) => {
        setAccentInsensitiveQuickFilter(productRef, target.value);
    }, []);


    return (
        <TemplateDefault title="Produtos" isLoading={isLoading}>
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="project"
                            label="Projeto"
                            options={projects}
                            value={project}
                            onChange={(value) => setProject(value)}
                        />
                    </div>

                    {
                        project &&
                        <>
                            <div className="col-span-3 text-gray-900">
                                <Button target={"primary"} onClick={addProduct}>
                                    <FaPlus/> Adicionar
                                </Button>
                            </div>
                            <InputForm id="search" col="col-span-6" label="Pesquisar" type="search" onChange={onFilterTextBoxChanged}/>
                        </>
                    }
                </div>

                <div className={"ag-theme-quartz grid col-span-12 mt-4"} style={{height: "calc(100vh - 185px)"}}>
                    <AgGridReact
                        ref={productRef}
                        rowHeight={32}
                        rowData={products}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        gridOptions={{localeText: LocalizationAgGrid}}
                        singleClickEdit={true}
                        onCellValueChanged={onCellValueChanged}
                        stopEditingWhenCellsLoseFocus={true}
                    />
                </div>

                {
                    project &&
                    <div className="col-span-12 text-gray-900 mt-3">
                        <div className="flex justify-end gap-3">
                            <Button target={"success"} onClick={save}>
                                <FaSave/> Salvar
                            </Button>
                        </div>
                    </div>
                }
            </Card>
        </TemplateDefault>
    );
};

export default Products;
